import React, { useContext, useMemo } from 'react'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import LuxPlusLandingBenefitCard, { type BenefitCardCta } from './LuxPlusLandingBenefitCard'
import LuxPlusDestinationCarousel from './LuxPlusDestinationCarousel'
import LuxPlusLandingMembershipSection from './LuxPlusLandingMembershipSection'
import LuxPlusLandingPartnerOffersSection from './LuxPlusLandingPartnerOffersSection'
import LuxPlusTestimonialCarousel from './LuxPlusTestimonialCarousel'
import LuxPlusLandingOfferCarousels from './LuxPlusLandingOfferCarousels'
import LuxPlusLandingFaqSection from './LuxPlusLandingFaqSection'
import LuxPlusLandingBottomCTA from './LuxPlusLandingBottomCTA'
import { useAppSelector } from 'hooks/reduxHooks'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LuxPlusPriceStack from '../LuxPlusPriceStack'
import Pill from 'LuxKit/Pill'
import LineLockOpenAltIcon from 'LuxKit/Icons/line/LineLockOpenAltIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Divider from 'LuxKit/Divider'
import Image from 'components/Common/Image'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LuxPlusLandingCtaButtons from './LuxPlusLandingCtaButtons'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import GeoContext from 'contexts/geoContext'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { getIsEnabledFlightsLuxPlusPricingInCurrentRegion } from 'luxPlus/selectors/featureToggle'
import useLuxPlusLandingAvailableProductList from 'luxPlus/hooks/useLuxPlusLandingAvailableProductList'
import LuxPlusLandingProductPillLink from './LuxPlusLandingProductPillLink'

const EXAMPLE_MEMBER_PRICE_AUD = 1999
const EXAMPLE_NON_MEMBER_PRICE_AUD = 2099

const HeroImageWrapper = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  width: 100%;
`

const HeroImage = styled(Image)`
  width: 100%;
  height: ${rem(220)};
  object-fit: cover;
  ${mediaQueryUp.tablet} {
    height: ${rem(380)};
  }

  ${mediaQueryUp.desktop} {
    img {
      transform: scaleX(-1);
    }
  }
`

const HeroOverlayCard = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(12)};
    position: absolute;
    top: ${rem(40)};
    inset-inline-start: ${rem(59)};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    border-radius: ${props => props.theme.borderRadius.M};
    padding: ${rem(28)};
    width: ${rem(301)};
    max-width: calc(100% - ${rem(48)});

    &.has-many-products {
      width: ${rem(330)};
    }
  }
`

const ProductPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    max-width: ${rem(228)};

    &.has-many-products {
      max-width: ${rem(274)};
    }
  }
`

const SavePill = styled.div`
  position: absolute;
  top: ${rem(-15)};
  inset-inline-end: ${rem(-6)};
`

const PriceOverlay = styled.div`
  position: absolute;
  top: ${rem(24)};
  inset-inline-end: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  background-color: ${transparentize(0.15, '#FFFFFF')};
  backdrop-filter: blur(${rem(8)});
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(16)} ${rem(12)};
  box-shadow:
    0 0 ${rem(17)} 0 ${transparentize(0.97, '#000000')},
    0 0 ${rem(5)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(2)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(1)} 0 ${transparentize(0.94, '#000000')};
  ${mediaQueryUp.desktop} {
    top: ${rem(205)};
    inset-inline-start: ${rem(300)};
    inset-inline-end: unset;

    &.has-many-products {
      top: ${rem(219)};
      inset-inline-start: ${rem(322)};
    }
  }

`

const MobileTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const BenefitsGrid = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SpecialOfferText = styled(CaptionTextBlock)`
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
`

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
  }
`

const TranslucentCard = styled.div`
  background:
    linear-gradient(0deg, ${transparentize(0.70, '#FFFFFF')} 0%, ${transparentize(0.70, '#FFFFFF')} 100%),
    ${transparentize(0.40, '#FFFFFF')};
  backdrop-filter: blur(${rem(4)});
  border: 1px solid ${transparentize(0.90, '#FFFFFF')};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const OverlayTag = styled(TranslucentCard)`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  padding: ${rem(6)} ${rem(12)};
  font-size: ${rem(14)};
  font-weight: 600;
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const BreakfastOverlayTag = styled(OverlayTag)`
  top: 10%;
  inset-inline-end: 25%;
`

const CocktailsOverlayTag = styled(OverlayTag)`
  bottom: 20%;
  inset-inline-start: 15%;
`

const OverlayCard = styled(TranslucentCard)`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  white-space: nowrap;
  padding: ${rem(16)} ${rem(20)};
`

const CenteredOverlayCard = styled(OverlayCard)`
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);

  [dir="rtl"] & {
    transform: translate(50%, -50%);
  }
`

const SupportOverlay = styled.div`
  position: absolute;
  bottom: ${rem(16)};
  inset-inline: ${rem(12)};
  display: flex;
  justify-content: flex-end;
`

const ChatBubble = styled(TranslucentCard)`
  display: flex;
  align-items: center;
  box-shadow:
    0 0 ${rem(1)} 0 ${transparentize(0.94, '#000000')},
    0 0 ${rem(2)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(5)} 0 ${transparentize(0.96, '#000000')},
    0 0 ${rem(17)} 0 ${transparentize(0.97, '#000000')};
  padding: ${rem(8)} ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const LogoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.brand.primary.lightest};
`

function LuxPlusLandingAboutSection() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { products: availableProducts } = useLuxPlusLandingAvailableProductList()
  const offer = useAppSelector(getBaseTierOfferWithJoiningFee)
  const isFlightsLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsLuxPlusPricingInCurrentRegion)
  const exampleMemberPrice = convertAudToRegion(EXAMPLE_MEMBER_PRICE_AUD, currentRegionCode)
  const exampleNonMemberPrice = convertAudToRegion(EXAMPLE_NON_MEMBER_PRICE_AUD, currentRegionCode)
  const exampleSaveAmount = exampleNonMemberPrice - exampleMemberPrice
  const hasManyProducts = availableProducts.length > 4
  const manyProductsClassName = clsx({ 'has-many-products': hasManyProducts })

  const breakfastTag = intl.formatMessage({
    id: 'kCl3XmfuUI0A7_6o155r',
    defaultMessage: '+ Breakfast',
    description: 'Overlay tag on benefit card showing bonus breakfast inclusion',
  })
  const becomeMemberLabel = intl.formatMessage({
    id: '3-tM77AW0JCHSsgPbV8Y',
    defaultMessage: 'Become a member',
    description: 'CTA button to start LuxPlus membership purchase on about section',
  })
  const startFreePreviewLabel = intl.formatMessage({
    id: 'UVip3IlIsg0kmlbsfdjr',
    defaultMessage: 'Start free preview',
    description: 'CTA button to start free preview on the LuxPlus about section',
  })
  const cocktailsTag = intl.formatMessage({
    id: 'xrwJv_IBBhsPK_3ThYrh',
    defaultMessage: '+ Cocktails',
    description: 'Overlay tag on benefit card showing bonus cocktails inclusion',
  })
  const memberOnlyAccessLabel = intl.formatMessage({
    id: 'g53lEXV911Lz5cVl4MIe',
    defaultMessage: 'Member only access',
    description: 'Overlay label on benefit card showing exclusive member access',
  })
  const chatBubbleText = intl.formatMessage({
    id: 'MmMW8e8jWtCdSdXgYwRX',
    defaultMessage: 'Hi Taylor, how can I help?',
    description: 'Example chat bubble text on concierge benefit card',
  })

  const bonusInclusionsCtas: Array<BenefitCardCta> = useMemo(() => (
    availableProducts
      .filter(product => product.type === 'hotels' || product.type === 'tours' || product.type === 'cruises' || product.type === 'toursAndCruises')
      .map(product => ({ label: product.label, to: product.to }))
  ), [availableProducts])
  const discountedFlightsCtas: Array<BenefitCardCta> = useMemo(() => (
    availableProducts
      .filter(product => product.type === 'flights')
      .map(product => ({ label: product.label, to: product.to }))
  ), [availableProducts])
  const everydaySavingsCtas: Array<BenefitCardCta> = useMemo(() => (
    availableProducts
      .filter(product => product.type === 'experiences')
      .map(product => ({ label: product.label, to: product.to }))
  ), [availableProducts])

  const benefits = useMemo(() => [
    {
      imageSrc: 'n0e7hjxi1nec5esx3qp',
      imageAlt: intl.formatMessage({
        id: 'DJCQfttjEM1A3a9pEMbU',
        defaultMessage: 'Cocktails and breakfast inclusions',
        description: 'Alt text for the bonus inclusions benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: '4-mAn895ot9FSrb2-gFo',
        defaultMessage: 'Bonus inclusions on stays and sailings',
        description: 'Title for the bonus inclusions benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: '9VBGu8fDw6wc2z9t7pH1',
        defaultMessage: 'Enjoy bonus hotel credit, daily breakfast, onboard spend and other extras.',
        description: 'Description for the bonus inclusions benefit card on LuxPlus page',
      }),
      ctas: bonusInclusionsCtas,
      imageOverlay:
        <CSSBreakpoint min="tablet">
          <BreakfastOverlayTag>{breakfastTag}</BreakfastOverlayTag>
          <CocktailsOverlayTag>{cocktailsTag}</CocktailsOverlayTag>
        </CSSBreakpoint>,
    },
    {
      imageSrc: 'pfc67g7och7mpy9k0lm',
      imageAlt: intl.formatMessage({
        id: 'wgCeRiJtR1B4jJTKDPmj',
        defaultMessage: 'Woman swimming in infinity pool',
        description: 'Alt text for the early access benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'HS3lRI3xl25DJwBXyC6_',
        defaultMessage: 'Access the best offers before anyone else',
        description: 'Title for the early access benefit card on the LuxPlus landing page',
      }),
      description: intl.formatMessage({
        id: 'HK2FAaZ7XioOiweTu1tV',
        defaultMessage: 'Be first to book our most popular hotels, with early access and member-only offers.',
        description: 'Description for the early access benefit card on LuxPlus page',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <CenteredOverlayCard>
          <LineLockOpenAltIcon size="M" />
          <BodyText variant="medium" colour="normal-contrast" align="center">{memberOnlyAccessLabel}</BodyText>
        </CenteredOverlayCard>
      </CSSBreakpoint>,
    },
    ...(isFlightsLuxPlusPricingEnabled ? [{
      imageSrc: '420cb679-5f16-4804-8723-7f3db3d76c9d',
      imageAlt: intl.formatMessage({
        id: 'KxgogmhSQug2az2vhzEP',
        defaultMessage: 'View from airplane window over a tropical island',
        description: 'Alt text for the exclusive flight rates benefit card image on the LuxPlus landing page',
      }),
      title: intl.formatMessage({
        id: 'TaBjtihfcwTzD6dQzvCC',
        defaultMessage: 'Discounted flights',
        description: 'Title for the exclusive flight rates benefit card on the LuxPlus landing page',
      }),
      description: intl.formatMessage({
        id: 'h3yj5_A5gXUx5tum8U0l',
        defaultMessage: 'Enjoy exclusive member-only prices on flights when you book as a member',
        description: 'Description for the exclusive flight rates benefit card on the LuxPlus landing page',
      }),
      ctas: discountedFlightsCtas,
    }] : []),
    ...(everydaySavingsCtas.length ? [{
      imageSrc: '5z0khcncrahy8qkhzor',
      imageAlt: intl.formatMessage({
        id: 'KLL3quM4uFTZWtfoUBrK',
        defaultMessage: 'Fine dining experience',
        description: 'Alt text for the everyday savings benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'JjmY5sdY8fvHz3mjn1Js',
        defaultMessage: 'Everyday savings beyond travel',
        description: 'Title for the everyday savings benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: 'GrgzR3o9t-zfR_gSDE51',
        defaultMessage: 'Enjoy exclusive discounts across dining, experiences and lifestyle brands.',
        description: 'Description for the everyday savings benefit card on LuxPlus page',
      }),
      ctas: everydaySavingsCtas,
    }] : []),
    {
      imageSrc: '4ybcsqdbvhb2vrx0zuf',
      imageAlt: intl.formatMessage({
        id: '_G2Sb7hsk4ygrnMkUPbF',
        defaultMessage: 'Customer support specialist',
        description: 'Alt text for the customer support benefit card image on LuxPlus page',
      }),
      title: intl.formatMessage({
        id: 'B-6kQaZbFVEs3PuSn6Fb',
        defaultMessage: 'Priority access to 24/7 customer support',
        description: 'Title for the customer support benefit card on LuxPlus page',
      }),
      description: intl.formatMessage({
        id: 'GRhbrgpSRfzoAgKaMGSn',
        defaultMessage: 'Dedicated travel specialists available whenever you or your travellers need help.',
        description: 'Description for the customer support benefit card on LuxPlus page',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <SupportOverlay>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LogoCircle>
              <LuxuryEscapesSmallLogo height={20} />
            </LogoCircle>
            <ChatBubble>{chatBubbleText}</ChatBubble>
          </Group>
        </SupportOverlay>
      </CSSBreakpoint>,
    },
  ], [intl, breakfastTag, cocktailsTag, memberOnlyAccessLabel, chatBubbleText, bonusInclusionsCtas, discountedFlightsCtas, everydaySavingsCtas, isFlightsLuxPlusPricingEnabled])

  return (<>
    <LayoutContainer>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <HeadingTextBlock variant="heading2" align="center">
            {intl.formatMessage({
              id: 'GWRseMVnjp0OQpRZjROy',
              defaultMessage: 'Unlimited benefits all year round',
              description: 'Heading for the LuxPlus about section benefit showcase',
            })}
          </HeadingTextBlock>
          {offer && <BodyTextBlock variant="large" colour="normal-contrast" align="center">
            <FormattedMessage
              id="9cmGvEF112Irdim2bhvN"
              defaultMessage="Enjoy member-only benefits for {price} per year, and a one-off joining fee of {joinFee}."
              description="Subheading showing LuxPlus subscription price and joining fee"
              values={{
                price: <FormatCurrency value={offer.price} />,
                joinFee: <FormatCurrency value={offer.joinFee} />,
              }}
            />
          </BodyTextBlock>}
          {offer && <SpecialOfferText variant="medium" align="center" startIcon={<LineFestivalsIcon />}>
            <FormattedMessage
              id="7ZUMSJjcF9tdVIDJ2XIO"
              defaultMessage="<strong>Special offer:</strong> No joining fee when you purchase an eligible {programName} offer (save {joinFee})"
              description="Special offer banner showing joining fee waiver when purchasing an eligible offer"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                joinFee: <FormatCurrency value={offer.joinFee} />,
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </SpecialOfferText>}
        </Group>

        <HeroImageWrapper>
          <HeroImage
            id="2sh8mhwtif27qzegy6jl"
            alt={intl.formatMessage({
              id: '0GP4MOWb0JiHW-sZ3NCC',
              defaultMessage: 'Luxury resort sunset pool',
              description: 'Alt text for the hero image in the LuxPlus about section',
            })}
          />
          <HeroOverlayCard className={manyProductsClassName}>
            <Heading variant="heading4">
              {intl.formatMessage({
                id: 'ykWf-2yMhxejAoJl1h3r',
                defaultMessage: 'Save hundreds with member-only prices',
                description: 'Heading on hero overlay card promoting member pricing',
              })}
            </Heading>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '1yX0ty0ikLOtX9760OEX',
                defaultMessage: 'Unlock lower prices on our best offers worldwide.',
                description: 'Description on hero overlay card introducing clickable product links for member pricing benefits',
              })}
            </BodyText>
            <ProductPills className={manyProductsClassName}>
              {availableProducts.map(product => (
                <LuxPlusLandingProductPillLink key={product.to} label={product.label} to={product.to} />
              ))}
            </ProductPills>
          </HeroOverlayCard>
          <PriceOverlay className={manyProductsClassName}>
            <SavePill>
              <Pill palette="success">
                <FormattedMessage
                  id="H9AlAHJGA4zhsqTUveV6"
                  defaultMessage="Save {amount}"
                  description="Pill showing example savings amount on the LuxPlus hero image"
                  values={{ amount: <FormatCurrency value={exampleSaveAmount} format="rounded" /> }}
                />
              </Pill>
            </SavePill>
            <LuxPlusPriceStack
              size="M"
              price={exampleNonMemberPrice}
              memberPrice={exampleMemberPrice}
              saleUnit=""
              signDisplay="always"
            />
          </PriceOverlay>
        </HeroImageWrapper>

        <MobileTextSection>
          <Heading variant="heading4">
            {intl.formatMessage({
              id: 'ykWf-2yMhxejAoJl1h3r',
              defaultMessage: 'Save hundreds with member-only prices',
              description: 'Heading on hero overlay card promoting member pricing',
            })}
          </Heading>
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: '1yX0ty0ikLOtX9760OEX',
              defaultMessage: 'Unlock lower prices on our best offers worldwide.',
              description: 'Description on hero overlay card introducing clickable product links for member pricing benefits',
            })}
          </BodyText>
          <ProductPills>
            {availableProducts.map(product => (
              <LuxPlusLandingProductPillLink key={product.to} label={product.label} to={product.to} />
            ))}
          </ProductPills>
        </MobileTextSection>

        <CSSBreakpoint max="tablet">
          <BenefitsGrid>
            {benefits.map((benefit) => (
              <LuxPlusLandingBenefitCard
                key={benefit.title}
                imageSrc={benefit.imageSrc}
                imageAlt={benefit.imageAlt}
                title={benefit.title}
                description={benefit.description}
                ctas={benefit.ctas}
                imageOverlay={benefit.imageOverlay}
              />
            ))}
          </BenefitsGrid>
        </CSSBreakpoint>

        <CSSBreakpoint min="desktop">
          <CardCarousel
            pageSize={benefits.length <= 4 ? benefits.length : 'auto'}
            desktopWidth={benefits.length <= 4 ? undefined : 'calc(25% - 36px)'}
            gap={24}
            snap="start"
            gutterStyle="default"
          >
            {benefits.map((benefit) => (
              <LuxPlusLandingBenefitCard
                key={benefit.title}
                imageSrc={benefit.imageSrc}
                imageAlt={benefit.imageAlt}
                title={benefit.title}
                description={benefit.description}
                ctas={benefit.ctas}
                imageOverlay={benefit.imageOverlay}
              />
            ))}
          </CardCarousel>
        </CSSBreakpoint>

        <CTASection>
          <LuxPlusLandingCtaButtons
            becomeMemberLabel={becomeMemberLabel}
            startFreePreviewLabel={startFreePreviewLabel}
            gap={12}
            horizontalAlign="center"
            switchRegionLogoColour="inherit"
          />
        </CTASection>
        <Divider kind="primary" />
      </Group>
    </LayoutContainer>
    <CSSBreakpoint min="tablet">
      <LuxPlusDestinationCarousel />
    </CSSBreakpoint>
    <LuxPlusLandingMembershipSection />
    <LuxPlusTestimonialCarousel />
    <LayoutContainer>
      <LuxPlusLandingPartnerOffersSection />
    </LayoutContainer>
    <LuxPlusLandingOfferCarousels />
    <LuxPlusLandingFaqSection />
    <LuxPlusLandingBottomCTA />
  </>
  )
}

export default LuxPlusLandingAboutSection
